import * as React from 'react';

import { Controller } from 'react-hook-form';

import { InputProps } from '../types';

export const MatrixInput: React.FC<React.PropsWithChildren<InputProps>> = ({ autofocus, field, onFocus, control }) => {
  const { id, required, matrix_options } = field;
  const { rows, columns } = matrix_options || { rows: [], columns: [] };
  const fieldName = id.toString();

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={{}}
      rules={{
        validate: required
          ? (value) => {
              return Object.keys(value || {}).length > 0;
            }
          : undefined
      }}
      render={({ onChange, value = {} }) => (
        <div className='overflow-x-auto'>
          <table className='min-w-max table-auto border-collapse'>
            <thead>
              <tr>
                {['', ...columns].map((header, index) => (
                  <th
                    key={header}
                    style={{ minWidth: '70px', maxWidth: '140px' }}
                    className={`h200 break-words py-2 text-center ${index === 0 ? 'sticky left-0 z-20 bg-white pr-4' : 'px-4'}`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx} className='odd:bg-white even:bg-gray-50'>
                  {Array(columns.length + 1)
                    .fill(0)
                    .map((cell, index) => {
                      const inputId = `${id}-${rowIdx}-${index}`;
                      const columnValue = index === 0 ? row : columns[index - 1];

                      return (
                        <td
                          key={index}
                          style={{ minWidth: '96px', maxWidth: '192px' }}
                          className={`h400 break-words py-2 text-sm ${index === 0 ? 'sticky left-0 z-0 bg-white pr-4' : 'px-4'}`}
                        >
                          {index === 0 ? (
                            row
                          ) : (
                            <div
                              key={index}
                              className='flex flex-shrink-0 items-center justify-center py-4'
                              style={{ minWidth: '70px' }}
                            >
                              <input
                                id={inputId}
                                type='radio'
                                className='focus-ring-custom-brand input-custom-brand h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
                                name={`matrix-${id}-${row}`}
                                value={columnValue}
                                checked={value[row] === columnValue}
                                autoFocus={rowIdx === 0 && index === 1 && autofocus}
                                onChange={(e) => {
                                  // Build new value maintaining row order
                                  const updatedAnswers = { ...value, [row]: e.target.value };

                                  // Rebuild object in the correct row order using Object.fromEntries
                                  const orderedEntries = rows
                                    .filter((rowKey) => updatedAnswers[rowKey] !== undefined)
                                    .map((rowKey) => [rowKey, updatedAnswers[rowKey]]);

                                  const orderedValue = Object.fromEntries(orderedEntries);

                                  onChange(orderedValue);
                                }}
                                onFocus={onFocus}
                                aria-describedby={`description-for-${id}`}
                                aria-label={`${row} - ${columnValue}`}
                              />
                            </div>
                          )}
                        </td>
                      );
                    })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    />
  );
};
